import React, {
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import {
  useCreateChatMutation,
  useCreateMessageMutation,
  useDeleteChatMutation,
} from "../../../api/chatApi";
import { ChatRequestTaskQue } from "../../../api/chatRequestTaskQueue";
import { Button } from "../../../components/tailwind";
import { Dropdown } from "../../../components/tailwind/dropdown/Dropdown";
import { ChatMessageForm } from "../../../customers/gpt/chat/ChatMessageForm";
import { ChatMessageList } from "../../../customers/gpt/chat/ChatMessageList";
import type {
  Message,
  MessageInteractionPhase,
} from "../../../customers/gpt/chat/types";
import { RootState } from "../../../utils/store";
import {
  CopyAssistantContext,
  CopyAssistantDispatchContext,
} from "../CopyAssistantContext/CopyAssistantContext";
import { CopyAssistantActionType } from "../CopyAssistantContext/types";
import { GptProviders, Prompt, Sentence } from "../types";
import { buildResultChatSystemMessage, fixLongName } from "../utils";

export const generatedResultChatToggleTestId = "GeneratedResultChat-toggle";
export const generatedResultChatPanelTestId = "GeneratedResultChat-panel";
export const generatedResultChatErrorTestId = "GeneratedResultChat-error";
export const generatedResultChatModelTestId = "GeneratedResultChat-model";

const REFINEMENT_GREETING: Message = {
  role: "assistant",
  text:
    "Ask me to change this text — shorter, a different tone, another angle — " +
    "then use the reply to replace it.",
  timeStamp: null,
};

const COMPOSER_CLASSES = [
  "tw-w-full",
  "tw-p-3",
  "tw-rounded-xl",
  "tw-border",
].join(" ");

type Props = {
  sentence: Sentence;
  prompt?: Prompt;
  sectionName: string;
  generatedText: string;
  languageName?: string;
  onApplyText: (text: string) => void;
};

/**
 * A conversation attached to one generated text, so it can be iterated on without
 * regenerating from the prompt form. Scoped to the generated text only —
 * translations are refined through the translate flow.
 */
export const GeneratedResultChat: React.FC<Props> = ({
  sentence,
  prompt,
  sectionName,
  generatedText,
  languageName,
  onApplyText,
}) => {
  const token = useSelector((state: RootState) => state.auth.token);
  const dispatch = useContext(CopyAssistantDispatchContext);
  const {
    models: { list: allModels },
  } = useContext(CopyAssistantContext);

  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [messageStep, setMessageStep] = useState(0);
  const [interactionPhase, setInteractionPhase] = useState<
    MessageInteractionPhase
  >("idle");

  const [createMessage] = useCreateMessageMutation();
  const [deleteChatRequest] = useDeleteChatMutation();
  const [
    createChatRequest,
    { isLoading: isCreatingChat },
  ] = useCreateChatMutation();

  const chatRequestTaskQue = useMemo(
    () => (token ? new ChatRequestTaskQue(token) : undefined),
    [token]
  );

  const chat = sentence.chat;
  const trailingMessage = chat?.messages[chat.messages.length - 1];
  const messageScrollAreaRef = useRef<HTMLDivElement>(null);

  // The trailing text and `messageStep` are dependencies because a streamed reply
  // grows in place, without the message count changing. `interactionPhase` is a
  // dependency too: leaving "awaiting reply" swaps the message from its animated,
  // button-less render to the final one with copy/apply actions, growing the
  // message's height without changing its text.
  useLayoutEffect(() => {
    const scrollArea = messageScrollAreaRef.current;
    if (!scrollArea) {
      return;
    }
    scrollArea.scrollTop = scrollArea.scrollHeight;
  }, [
    isOpen,
    chat?.messages.length,
    trailingMessage?.text,
    messageStep,
    interactionPhase,
  ]);

  // ChatClient posts to OpenRouter only, so other providers cannot be offered
  // even though the endpoint returns them.
  const openrouterModels = useMemo(
    () =>
      allModels.filter(({ provider }) => provider === GptProviders.OPENROUTER),
    [allModels]
  );

  // Preferred over the backend's fallback, which is whichever row carries the
  // highest `order`. Undefined while models load, in which case the backend picks.
  const defaultModelName =
    openrouterModels.find(({ default_selected }) => default_selected)
      ?.model_name ?? openrouterModels[openrouterModels.length - 1]?.model_name;

  const modelDropdownOptions = useMemo(
    () =>
      openrouterModels.map(({ model_name, display_name }) => ({
        key: model_name,
        value: model_name,
        text: display_name || model_name,
      })),
    [openrouterModels]
  );

  const startChat = async (): Promise<void> => {
    const systemMessage = buildResultChatSystemMessage({
      instruction: sentence.generationInfo?.instruction ?? prompt?.instruction,
      inputText: sentence.generationInfo?.inputText,
      generatedText,
      languageName,
      promptName: prompt?.name,
      sectionName,
    });

    // Truncated to keep "Refine: " plus the name inside Chat.name's 100 characters.
    const chatName = `Refine: ${fixLongName(
      prompt?.name || sectionName || "generated text",
      80
    )}`;

    // Deliberately not mirrored into local state: it is instruction, not
    // conversation, and should not be rendered in the message list.
    const response = await createChatRequest({
      name: chatName,
      model_name: defaultModelName,
      // Keeps it out of the chat page's list and lets it resume after a reload.
      text_block_id: sentence.sectionInfo?.textBlockId,
      messages: [{ role: "system", text: systemMessage }],
    }).unwrap();

    dispatch({
      type: CopyAssistantActionType.SET_SENTENCE_CHAT,
      payload: {
        id: sentence.id,
        chat: {
          id: response.id,
          modelName: response.model_name,
          messages: [],
          knownText: generatedText,
        },
      },
    });
  };

  const handleToggle = async (): Promise<void> => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }

    setErrorMessage(null);
    setIsOpen(true);

    // An unspoken chat is re-seeded on open so context picks up any edits since.
    // Once there are messages, re-seeding would throw them away.
    if (chat?.messages.length) {
      return;
    }

    try {
      if (chat) {
        await deleteChatRequest({ chat_id: chat.id });
      }
      await startChat();
    } catch (error) {
      console.error("Failed to start result chat", error);
      setErrorMessage("Could not start the chat. Please try again.");
      setIsOpen(false);
    }
  };

  /**
   * `create_message` writes the model onto the chat with every message, so a switch
   * applies from the next turn. Kept on the chat so it survives the panel being
   * unmounted by paging between variants.
   */
  const handleModelChange = (modelName: string): void => {
    if (chat) {
      dispatch({
        type: CopyAssistantActionType.SET_SENTENCE_CHAT,
        payload: { id: sentence.id, chat: { ...chat, modelName } },
      });
    }
  };

  /**
   * The text can be edited while the chat is open, and the model only knows what
   * has been sent to it, so carry the current text when it has moved on.
   */
  const createMessageWithCurrentText: typeof createMessage = (arg) => {
    if (!chat || chat.knownText === generatedText) {
      return createMessage(arg);
    }

    return createMessage({
      ...arg,
      text:
        "The text has been edited since your last reply. It now reads:\n" +
        `${generatedText}\n\n${arg.text}`,
    });
  };

  const handleMessageAdded = (message: Message): void => {
    dispatch({
      type: CopyAssistantActionType.ADD_SENTENCE_CHAT_MESSAGE,
      payload: {
        id: sentence.id,
        message,
        // Only added once created server side, so recording it here means a failed
        // send cannot convince us the model saw an edit it never received.
        ...(message.role === "user" ? { knownText: generatedText } : {}),
      },
    });
  };

  const handleAssistantStreamUpdate = (message: Message): void => {
    dispatch({
      type: CopyAssistantActionType.ADD_SENTENCE_CHAT_MESSAGE,
      payload: { id: sentence.id, message, replaceTrailingAssistant: true },
    });
  };

  const isAwaitingFirstToken =
    interactionPhase !== "idle" && trailingMessage?.role !== "assistant";

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      <div className="tw-flex tw-items-center tw-gap-3">
        <Button
          size="sm"
          variant="secondary"
          compact
          icon
          disabled={!generatedText}
          loading={isCreatingChat}
          data-testid={generatedResultChatToggleTestId}
          onClick={(): void => void handleToggle()}
        >
          <i className="material-icons">forum</i>
          {isOpen ? "Hide chat" : "Improve with chat"}
        </Button>
        {errorMessage && (
          <span
            className="tw-text-sm tw-text-red-600"
            data-testid={generatedResultChatErrorTestId}
          >
            {errorMessage}
          </span>
        )}
      </div>

      {isOpen && chat && chatRequestTaskQue && (
        <div
          className="tw-flex tw-flex-col tw-gap-2 tw-rounded-xl tw-border tw-p-3"
          data-testid={generatedResultChatPanelTestId}
        >
          {modelDropdownOptions.length > 0 && (
            <div className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-text-sm tw-text-gray-600">Model:</span>
              <Dropdown<string>
                variant="alternate"
                testIdBase={generatedResultChatModelTestId}
                options={modelDropdownOptions}
                value={chat.modelName ?? null}
                disabled={interactionPhase !== "idle"}
                onChange={(next): void => {
                  const modelName = Array.isArray(next) ? next[0] : next;
                  if (modelName) handleModelChange(modelName);
                }}
              />
            </div>
          )}

          <div
            ref={messageScrollAreaRef}
            className="tw-max-h-96 tw-overflow-y-auto"
          >
            <ChatMessageList
              messages={chat.messages}
              messageStep={messageStep}
              assistantReplyStreaming={interactionPhase === "awaiting reply"}
              onUseAssistantText={onApplyText}
              greeting={REFINEMENT_GREETING}
            />
            {isAwaitingFirstToken && (
              <p className="tw-ml-4 tw-animate-pulse tw-text-sm tw-text-gray-500">
                Thinking...
              </p>
            )}
          </div>

          <ChatMessageForm
            chatId={chat.id}
            chatRequestTaskQue={chatRequestTaskQue}
            selectedModel={{
              model_name: chat.modelName,
              display_name:
                openrouterModels.find(
                  ({ model_name }) => model_name === chat.modelName
                )?.display_name ?? chat.modelName,
              supported_parameters: openrouterModels.find(
                ({ model_name }) => model_name === chat.modelName
              )?.supported_parameters,
            }}
            interactionPhase={interactionPhase}
            createMessage={createMessageWithCurrentText}
            setResponsePhase={setInteractionPhase}
            onMessageAdded={handleMessageAdded}
            onAssistantStreamUpdate={handleAssistantStreamUpdate}
            onNonStreamResponse={(): void => setMessageStep((step) => step + 1)}
            className={COMPOSER_CLASSES}
            formId={`result-chat-form-${chat.id}`}
            textareaId={`result-chat-textarea-${chat.id}`}
          />
        </div>
      )}
    </div>
  );
};
